<template>
  <div class="flex flex-grow pb-12">
    <empty-view v-if="!action" :text="emptyViewText" class="h-full w-full my-auto"/>

    <email-editor v-else-if="isEmailBot"
                  class="mt-5 overflow-y-auto"/>

    <div v-else class="w-full">
      <div class="flex justify-center mt-5 px-6">
        <section-picker ref="picker"
          title-open="Choose..."
          title-closed="Add..."
          subtitle-open="Choose a component"
          subtitle-closed="Text, Image, Buttons..."
          :picker-items="pickerItems"
          class="w-full"/>

        <delete-message-button :bot-id="botId" />
      </div>

      <text-section v-if="text !== null" class="mt-12 px-12"/>

      <image-section v-if="imageUrl !== null" class="mt-12 px-12"/>

      <buttons-section v-if="buttons.length > 0" :bot-id="botId" class="mt-5 px-12"/>

      <quick-replies-section v-if="quickReplies.length > 0" :bot-id="botId" class="mt-5 px-12"/>
    </div>
  </div>
</template>

<script>
import TextSection from './TextSection'
import ImageSection from './ImageSection'
import ButtonsSection from './ButtonsSection'
import QuickRepliesSection from './QuickRepliesSection'
import SectionPicker from './SectionPicker'
import EmptyView from '../EmptyView'
import contentUtils from '@/utils/content.utils'
import toaster from '@/utils/toaster'
import DeleteMessageButton from './DeleteMessageButton'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import templateModule from '@/store/modules/template.module'
import answerController from '@/controllers/answer.controller'
import templateController from '@/controllers/template.controller'
import isEmpty from 'ramda/src/isEmpty'
import isNil from 'ramda/src/isNil'
import last from 'ramda/src/last'
import { SECTION_FOCUSES } from '@/store/constants'
import RandomUtils from '@/utils/random.utils'
import outputChatBubbleModule from '@/store/modules/output.chat-bubble.module'
import botModule from '@/store/modules/bot.module'
import EmailEditor from '@/components/editor/EmailEditor.vue'
import { ANSWER_END } from '@/utils/bot-json.constants'
import stateTreeUtils from '@/utils/state-tree.utils'

const randomString = RandomUtils.randomString

export default {
  name: 'ChatEditor',

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  components: {
    ButtonsSection,
    DeleteMessageButton,
    EmptyView,
    QuickRepliesSection,
    SectionPicker,
    TextSection,
    ImageSection,
    EmailEditor
  },

  data() {
    return {
      pickerItems: [
        {
          title: 'Text',
          icon: 'txt',
          onClick: this.addTextSection,
          disabled: () => this.text !== null || !isNil(this.imageUrl),
          disabledHint: () => this.getDisabledHintByType('Text'),
          order: 0
        },
        {
          title: 'Buttons',
          icon: 'cta',
          onClick: this.addButtonsSection,
          disabled: () => !isEmpty(this.buttons) || !isNil(this.imageUrl),
          disabledHint: () => this.getDisabledHintByType('Buttons'),
          order: 1
        },
        {
          title: 'Quick Replies',
          icon: 'qr',
          onClick: this.addQuickRepliesSection,
          disabled: this.isQuickRepliesDisabled,
          disabledHint: () => this.getDisabledHintByType('Quick Replies'),
          order: 2
        },
        {
          title: 'Image',
          icon: 'img',
          onClick: this.addImageSection,
          disabled: this.isImageDisabled,
          disabledHint: this.getImageDisabledHint,
          order: 3
        }
      ]
    }
  },

  computed: {
    isEmailBot: botModule.isEmailBot,
    bubbleSelected: chatBubbleModule.bubbleSelected,
    stateTree: chatBubbleModule.stateTree,

    actionId() {
      return this.bubbleSelected.actionId
    },

    action() {
      if (this.actionId === null) return null // WAT doing actions[null] is translated to actions['null']

      return conversationModule.getAction(this.stateId, this.actionId)
    },

    buttons() {
      return contentUtils.getButtons(this.action)
    },

    emptyViewText() {
      const doesRouteInclude = text => this.$route.name.includes(text)
      const composeCtaString = ctaText => `Click "${ctaText}" to start...`

      if (doesRouteInclude('event')) return composeCtaString('React to event')

      if (doesRouteInclude('block')) return composeCtaString('Begin block')

      return composeCtaString('Answer the request')
    },

    quickReplies: {
      get() {
        return contentUtils.getQuickReplies(this.action)
      },
      set(quickReplies) {
        this.addOutputAction(contentUtils.createContent(this.text, this.buttons, quickReplies))
      }
    },

    stateId() {
      if (this.bubbleSelected.templateStateId) return this.bubbleSelected.templateStateId

      return this.bubbleSelected.stateId
    },

    text: {
      get() {
        return contentUtils.getText(this.action)
      },
      set(text) {
        this.addOutputAction(contentUtils.createContent(text, this.buttons, this.quickReplies))
      }
    },

    imageUrl: {
      get() {
        return contentUtils.getImageUrl(this.action)
      },
      set(imageUrl) {
        this.addOutputAction(contentUtils.createContent(this.text, this.buttons, this.quickReplies, imageUrl))
      }
    }
  },

  methods: {
    addOutputAction(content) {
      conversationModule.addOutputAction(this.actionId, content)
    },

    addTextSection() {
      this.text = ''
      this.$refs.picker.close()
    },

    addImageSection() {
      this.imageUrl = ''
      this.$refs.picker.close()
    },

    addButtonsSection() {
      const actionWithId = { id: this.actionId, ...this.action }
      outputChatBubbleModule.addActionButton(this.stateId, actionWithId, 'Button 1')

      this.$nextTick(() => {
        chatBubbleModule.focusSection(SECTION_FOCUSES.BUTTON + '0')
      })

      this.$refs.picker.close()
    },

    addQuickRepliesSection() {
      this.addQuickReply('Quick Reply 1')

      this.$nextTick(() => {
        chatBubbleModule.focusSection(SECTION_FOCUSES.QUICK_REPLY + '0')
      })

      this.$refs.picker.close()
    },

    // TODO should be done in QuickRepliesSection.vue
    addQuickReply(title) {
      const qrPayload = 'qr_' + this.actionId + '_' + randomString(5)

      if (templateModule.isTemplateEditor()) {
        try {
          const blockId = templateModule.getTemplateId()
          templateController.updateAnswerEnd(this.stateId, ANSWER_END.FIND_INTENT, this.bubbleSelected, blockId)
          templateController.addQuickReply(this.stateId, this.actionId, title, qrPayload, blockId)
        } catch (e) {
          toaster.error('Quick Replies section cannot be added because the Template is used in Answers that have follow-on messages.')
        }
      } else {
        answerController.addQuickReply(this.stateId, this.actionId, title, qrPayload)
      }
    },

    isImageDisabled() {
      return !isNil(this.imageUrl) || !isNil(this.text) || !isEmpty(this.buttons) || !isEmpty(this.quickReplies)
    },

    getImageDisabledHint() {
      if (!isNil(this.imageUrl)) return 'This message already contains an Image component.'

      return 'Image cannot be combined with Text, Buttons or Quick Replies.'
    },

    isQuickRepliesDisabled() {
      if (!isEmpty(this.quickReplies) || !isNil(this.imageUrl)) return true

      return conversationModule.isLastState(this.stateId) || last(conversationModule.getState(this.stateId).actions) !== this.actionId
    },

    getDisabledHintByType(type) {
      if (!isNil(this.imageUrl)) return `You cannot combine ${type} with an Image.`

      if (type === 'Quick Replies') {
        if (!isEmpty(this.quickReplies)) return 'This message already contains Quick Replies component.'

        const node = stateTreeUtils.findNode(this.stateTree, this.bubbleSelected)
        return node.children[0].node.isCondition ?
          'Quick Replies are not compatible with Message Transition "Exit the Condition".' :
          'Quick Replies component can only be added to the end of the answer.'
      }

      return `This message already contains ${type} component.`
    }
  }
}
</script>
